import type { ShelfDeadSpan } from "@CommonUtils/pog/shelfMerchandising";
import { PsaFixtureType } from "@CommonTypes/psa/FixtureRecord";

/** A box in PSA planogram space: x/width across, y/height up, z/depth front to back. */
interface PsaBox {
  x: number;
  width: number;
  y: number;
  height: number;
  z?: number | null;
  depth?: number | null;
}

/** A shelf as the PSA places it; height is the board's thickness, merch the product space above it. */
export interface DeadSpanShelfGeometry extends PsaBox {
  merch?: number | null;
  leftOverhang?: number | null;
  rightOverhang?: number | null;
}

/** A PSA OBSTRUCTION fixture (type 10). */
export type ObstructionGeometry = PsaBox;

interface PsaFixtureLike {
  type: number | null;
  x: number | null;
  width: number | null;
  y: number | null;
  height: number | null;
  z?: number | null;
  depth?: number | null;
}

/** The OBSTRUCTION fixtures of a PSA with usable geometry. */
export const toObstructionGeometry = (fixtures: readonly PsaFixtureLike[]): ObstructionGeometry[] =>
  fixtures
    .filter(
      (fixture) =>
        Number(fixture.type) === PsaFixtureType.OBSTRUCTION &&
        [fixture.x, fixture.width, fixture.y, fixture.height].every((value) => value != null),
    )
    .map((fixture) => ({
      x: Number(fixture.x),
      width: Number(fixture.width),
      y: Number(fixture.y),
      height: Number(fixture.height),
      z: fixture.z == null ? null : Number(fixture.z),
      depth: fixture.depth == null ? null : Number(fixture.depth),
    }))
    .filter(({ x, width, y, height }) => [x, width, y, height].every(Number.isFinite));

const overlaps = (a0: number, a1: number, b0: number, b1: number) => a0 < b1 && b0 < a1;

// Depth is only compared when both boxes state it; a PSA that leaves it out still cuts.
const overlapsInDepth = (a: PsaBox, b: PsaBox) =>
  !(Number(a.depth) > 0 && Number(b.depth) > 0) ||
  overlaps(Number(a.z) || 0, (Number(a.z) || 0) + Number(a.depth), Number(b.z) || 0, (Number(b.z) || 0) + Number(b.depth));

const roundTo = (value: number, decimals: number) => Number(value.toFixed(decimals));

// How far above a board an obstruction's foot may be and still stand on it.
const STANDS_ON_TOLERANCE = 0.5;

/**
 * Whether the obstruction takes floor space from this shelf's products: it
 * passes through the board, stands on it, or hangs in the product space above
 * it. A wide one floating above the board (a header, a beam) only limits how
 * tall products can be, so it does not cut width.
 */
const blocksShelfProducts = (obstruction: ObstructionGeometry, shelf: DeadSpanShelfGeometry) => {
  const boardTop = shelf.y + shelf.height;
  const foot = obstruction.y;
  const head = obstruction.y + obstruction.height;
  if (overlaps(foot, head, shelf.y, boardTop)) return true;
  if (Math.abs(foot - boardTop) <= STANDS_ON_TOLERANCE) return true;
  const zoneTop = boardTop + Math.max(0, Number(shelf.merch) || 0);
  return foot > boardTop && foot < zoneTop && obstruction.height >= obstruction.width;
};

/**
 * Whether an obstruction is as wide as the shelf itself (99% of it or more),
 * which is how a PSA's fixture panels read; a pole or duct is narrower.
 */
export const isFixturePanelWidth = (obstructionWidth: number, shelfWidth: number) =>
  obstructionWidth >= shelfWidth * 0.99;

/**
 * The obstruction boxes that write one strip back to a PSA, in shelf-relative x.
 *
 * A strip is one box, unless it is so wide the importer would take that box
 * for a fixture panel and drop it — a merged run of poles, or a board
 * obstructed end to end. That one is written as two halves that overlap a
 * little, each well under the panel width, which the importer merges back into
 * the one strip. Real panels are untouched: nothing here changes how a PSA's
 * own records are read.
 */
export const psaObstructionSpansFor = (
  span: ShelfDeadSpan,
  shelfWidth: number,
): ShelfDeadSpan[] => {
  if (!isFixturePanelWidth(span.width, shelfWidth)) return [span];
  const half = Math.floor(span.width / 2);
  const overlap = Math.max(1, Math.round(span.width * 0.01));
  return [
    { x: span.x, width: half + overlap },
    { x: span.x + half - overlap, width: span.width - half + overlap },
  ];
};

/**
 * The strips of a shelf that obstructions block, in shelf-relative x, merged
 * and left to right.
 *
 * An obstruction counts only when it overlaps the shelf in depth and blocks
 * its products (see blocksShelfProducts): a back panel, side panel or
 * baseboard behind the shelves, and a header or beam above them, do not cut
 * width. One that covers the whole shelf is the fixture itself, not a pole,
 * and is ignored too. A negative left/right overhang (how Space Planning
 * shortens a shelf) is a strip on that edge.
 */
export const computeShelfDeadSpans = (
  shelf: DeadSpanShelfGeometry,
  obstructions: readonly ObstructionGeometry[],
  decimals = 2,
): ShelfDeadSpan[] => {
  const shelfRight = shelf.x + shelf.width;
  const spans: ShelfDeadSpan[] = [];

  for (const obstruction of obstructions) {
    const left = Math.max(obstruction.x, shelf.x);
    const right = Math.min(obstruction.x + obstruction.width, shelfRight);
    // Judged by the obstruction's own width, so a header that spills a sliver
    // into the next bay is not a pole there either.
    const coversWholeShelf = isFixturePanelWidth(obstruction.width, shelf.width);
    if (
      right > left &&
      !coversWholeShelf &&
      blocksShelfProducts(obstruction, shelf) &&
      overlapsInDepth(obstruction, shelf)
    ) {
      spans.push({ x: left - shelf.x, width: right - left });
    }
  }
  if ((shelf.leftOverhang ?? 0) < 0) {
    spans.push({ x: 0, width: -Number(shelf.leftOverhang) });
  }
  if ((shelf.rightOverhang ?? 0) < 0) {
    spans.push({ x: shelf.width + Number(shelf.rightOverhang), width: -Number(shelf.rightOverhang) });
  }

  spans.sort((a, b) => a.x - b.x);
  const merged: ShelfDeadSpan[] = [];
  for (const span of spans) {
    const x = Math.max(0, span.x);
    const right = Math.min(shelf.width, span.x + span.width);
    if (right <= x) continue;
    const last = merged[merged.length - 1];
    if (last && x <= last.x + last.width) {
      last.width = Math.max(last.x + last.width, right) - last.x;
    } else {
      merged.push({ x, width: right - x });
    }
  }
  return merged.map((span) => ({ x: roundTo(span.x, decimals), width: roundTo(span.width, decimals) }));
};

/** Decimals the stored strips keep in PSA units before their one rounding: past float noise, short of rounding. */
const STORED_SPAN_DECIMALS = 6;
/** How close to the shelf end a strip's raw right edge is taken to reach it. */
const SHELF_END_TOLERANCE = 1e-6;

/** PSA units to the stored integer units (hundredths), as shelf widths are stored. */
const toStoredUnits = (value: number) => Math.round(Number(value) * 100);

/**
 * The strips of `computeShelfDeadSpans` in stored units: the one derivation
 * the backend's PSA import and the frontend's PSA builder both store.
 *
 * Both edges are rounded, then the width between them. Rounding x and width
 * apart (or rounding twice) can land the right edge one short of the shelf
 * end, leaving a live sliver there and freeing the side the pole closes; a
 * strip that reaches the end ends where the stored width does.
 */
export const computeStoredShelfDeadSpans = (
  shelf: DeadSpanShelfGeometry,
  obstructions: readonly ObstructionGeometry[],
): ShelfDeadSpan[] => {
  const shelfWidth = Number(shelf.width);
  const storedShelfWidth = toStoredUnits(shelfWidth);
  return computeShelfDeadSpans(shelf, obstructions, STORED_SPAN_DECIMALS).map((span) => {
    const left = toStoredUnits(span.x);
    const rawRight = span.x + span.width;
    const right =
      rawRight >= shelfWidth - SHELF_END_TOLERANCE ? storedShelfWidth : toStoredUnits(rawRight);
    return { x: left, width: right - left };
  });
};

export const deadSpansWidth = (spans: readonly ShelfDeadSpan[]) =>
  spans.reduce((total, span) => total + span.width, 0);
